import { BLACK_BLURHASH } from '../avatar'
import { companyKey, companyListKey } from '../keys'
import { type CompanyList } from '../types'
import { useQueryClient } from '@tanstack/react-query'
import {
  type CompanyId,
  Logger,
  type TinyCompany,
  type CompanyData,
  type PartialCompanyData,
  stripUndefined,
  toTinyCompany,
} from '@terros/common'

const logger = new Logger('useCompanyCache')

// logger.level = 'verbose'

export const applyCompanyUpdate = (company: TinyCompany, update: PartialCompanyData): TinyCompany => ({
  ...company,
  ...stripUndefined(update),
  internalOwnerId:
    update.internalOwnerId === null ? undefined : (update.internalOwnerId ?? company.internalOwnerId),
  salesforceAccountId:
    update.salesforceAccountId === null ? undefined : (update.salesforceAccountId ?? company.salesforceAccountId),
})

export function useCompanyCache() {
  const queryClient = useQueryClient()
  return {
    clearItem: (companyId: CompanyId) => {
      logger.verbose('invalidateQueries companyKey', companyId)
      queryClient.invalidateQueries({ queryKey: companyKey(companyId) })
    },
    clearList: () => {
      logger.verbose('invalidateQueries companyListKey')
      queryClient.invalidateQueries({ queryKey: companyListKey() })
    },
    getItem: (companyId?: CompanyId): TinyCompany | undefined => {
      if (!companyId) return
      logger.verbose('get teamKey', companyId)
      return queryClient.getQueryData<CompanyList>(companyListKey())?.find((t) => companyId === t.companyId)
    },
    updateItem: (company: TinyCompany): void => {
      queryClient.setQueriesData<CompanyList>({ queryKey: companyListKey() }, (existing) =>
        existing?.map((item) => (item.companyId === company.companyId ? company : item))
      )
    },
    updateCompany: (company: CompanyData): void => {
      queryClient.setQueryData<CompanyData>(companyKey(company.companyId), company)
      queryClient.setQueriesData<CompanyList>({ queryKey: companyListKey() }, (existing) =>
        existing?.map((item) => (item.companyId === company.companyId ? toTinyCompany(company) : item))
      )
    },
    updateAvatar: (companyId: CompanyId): void => {
      queryClient.setQueryData<CompanyData>(companyKey(companyId), (existing) => {
        if (!existing) return
        return {
          ...existing,
          avatarUrl: existing.avatarUrl + '?v=' + Date.now(),
          avatarBlurhash: existing.avatarBlurhash ?? BLACK_BLURHASH,
        }
      })
    },
    updateIcon: (companyId: CompanyId): void => {
      queryClient.setQueryData<CompanyData>(companyKey(companyId), (existing) => {
        if (!existing) return
        return {
          ...existing,
          iconUrl: existing.iconUrl + '?v=' + Date.now(),
          iconBlurhash: existing.iconBlurhash ?? BLACK_BLURHASH,
        }
      })
    },
  }
}
